// Fleet Reports - dashboard Settings (owner decision 2026-07-23).
// The two user-tunable knobs live HERE, on the dashboard itself, not in an
// admin console: the fuel price (drives the Fuel Cost metric) and which of the
// fleet's vehicles are included in the report (all service devices are vehicles
// by default; de-select to exclude). Saves via PUT /aggregation/fleet-settings,
// which persists to the licence and takes effect on the next Load.
import React from "react";
import { Card, Grid, Button, TextField, Checkbox, FormControlLabel } from "@mui/material";
import { useSelector } from "react-redux";
import useFleetKpiConfig from "hooks/useFleetKpiConfig";
import { useUpdateFleetSettingsMutation } from "services/reporting";

const sectionTitle = (text) => (
  <p style={{ fontSize: "15px", fontWeight: "bold", color: "#333333", margin: "0 0 12px 0", textTransform: "uppercase" }}>
    {text}
  </p>
);

export default function SettingsSection() {
  const fleet = useFleetKpiConfig();
  const metaDataValue = useSelector((state) => state.metaData);
  const accent = metaDataValue.branding?.secondaryColor || "#607d8b";
  const [update, { isLoading }] = useUpdateFleetSettingsMutation();

  // Local drafts, seeded from the config and re-seeded whenever it refreshes
  // (the mutation invalidates the config query, which re-dispatches the slice).
  const [price, setPrice] = React.useState(fleet.fuelPrice === null ? "" : String(fleet.fuelPrice));
  const [currency, setCurrency] = React.useState(fleet.fuelCurrency);
  const [excluded, setExcluded] = React.useState(new Set(fleet.excludedVehicles.map(String)));
  const [message, setMessage] = React.useState(null); // {ok, text}
  React.useEffect(() => {
    setPrice(fleet.fuelPrice === null ? "" : String(fleet.fuelPrice));
    setCurrency(fleet.fuelCurrency);
    setExcluded(new Set(fleet.excludedVehicles.map(String)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fleet.fuelPrice, fleet.fuelCurrency, JSON.stringify(fleet.excludedVehicles)]);

  const available = fleet.availableVehicles;
  const selectedCount = available.filter((v) => !excluded.has(String(v.sensorId))).length;
  const priceNum = price.trim() === "" ? null : Number(price);
  const priceValid = priceNum === null || (Number.isFinite(priceNum) && priceNum >= 0);
  const currencyValid = /^[A-Za-z]{3}$/.test(currency);

  const toggle = (sensorId) => {
    const next = new Set(excluded);
    const id = String(sensorId);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExcluded(next);
    setMessage(null);
  };

  const save = async () => {
    setMessage(null);
    const body = { fuelPrice: priceNum, fuelCurrency: currency.toUpperCase() };
    if (fleet.vehicleSelectionEditable) body.excludedVehicles = [...excluded];
    try {
      const res = await update({ token: window.localStorage.getItem("token"), body }).unwrap();
      setMessage({ ok: true, text: "Settings saved - they apply to the next report Load." });
      if (res && res.payload && Array.isArray(res.payload.excludedVehicles)) {
        setExcluded(new Set(res.payload.excludedVehicles.map(String)));
      }
    } catch (e) {
      setMessage({ ok: false, text: (e && e.data && e.data.message) || "Could not save settings. Please try again." });
    }
  };

  return (
    <div>
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Card style={{ padding: "16px", height: "100%" }}>
            {sectionTitle("Fuel price")}
            <p style={{ fontSize: "13px", color: "grey", margin: "0 0 12px 0" }}>
              Used by the Fuel Cost page: cost = consumption x price per litre. Leave empty to turn Fuel Cost off.
            </p>
            <div style={{ display: "flex", gap: "12px", alignItems: "flex-start", flexWrap: "wrap" }}>
              <TextField
                label="Price per litre"
                size="small"
                value={price}
                onChange={(e) => { setPrice(e.target.value); setMessage(null); }}
                error={!priceValid}
                helperText={priceValid ? " " : "Enter a non-negative number, or leave empty"}
                inputProps={{ inputMode: "decimal" }}
                style={{ width: "160px" }}
              />
              <TextField
                label="Currency"
                size="small"
                value={currency}
                onChange={(e) => { setCurrency(e.target.value.toUpperCase()); setMessage(null); }}
                error={!currencyValid}
                helperText={currencyValid ? " " : "3-letter code, e.g. XCD"}
                inputProps={{ maxLength: 3 }}
                style={{ width: "110px" }}
              />
            </div>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card style={{ padding: "16px", height: "100%" }}>
            {sectionTitle("Vehicles in the report")}
            {fleet.vehicleSelectionEditable ? (
              <>
                <p style={{ fontSize: "13px", color: "grey", margin: "0 0 8px 0" }}>
                  Every device on <b>{fleet.serviceName || "the fleet solution"}</b> is included by default - untick a
                  vehicle to leave it out of the report. At least one must stay selected.
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0 18px" }}>
                  {available.map((v) => (
                    <FormControlLabel
                      key={v.sensorId}
                      control={
                        <Checkbox
                          size="small"
                          color="secondary"
                          checked={!excluded.has(String(v.sensorId))}
                          onChange={() => toggle(v.sensorId)}
                        />
                      }
                      label={<span style={{ fontSize: "14px" }}>{v.name || v.sensorId}</span>}
                    />
                  ))}
                  {available.length === 0 ? (
                    <p style={{ fontSize: "14px", color: "#c7c7c7", margin: 0 }}>No devices found on the fleet solution.</p>
                  ) : null}
                </div>
                {selectedCount === 0 && available.length > 0 ? (
                  <p style={{ fontSize: "13px", color: "#bf3535", margin: "8px 0 0 0" }}>
                    At least one vehicle must remain selected.
                  </p>
                ) : null}
              </>
            ) : (
              <p style={{ fontSize: "14px", color: "#c7c7c7", margin: 0 }}>
                Vehicle selection is managed by your solution administrator for this fleet.
              </p>
            )}
          </Card>
        </Grid>
      </Grid>

      <div style={{ display: "flex", alignItems: "center", gap: "16px", marginTop: "20px" }}>
        <Button
          variant="contained"
          color="secondary"
          disabled={isLoading || !priceValid || !currencyValid || (fleet.vehicleSelectionEditable && available.length > 0 && selectedCount === 0)}
          onClick={save}
        >
          {isLoading ? "Saving…" : "Save Settings"}
        </Button>
        {message ? (
          <p style={{ fontSize: "13px", margin: 0, color: message.ok ? accent : "#bf3535" }}>{message.text}</p>
        ) : null}
      </div>
    </div>
  );
}
